import { useEffect, useState } from 'react';

import { useApiLocationGeocodeRetrieve } from '@rover/rsdk/src/apiClient/latest/api-location-geocode-retrieve';
import type { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest/model/userInformationAddress';

import { mapGeocodeResponseToAddress } from '../utils';

type UseAddressGeocodeReturn = {
  geocodedAddress: Partial<UserInformationAddress>;
  geocodeAddress: (placeId: string | null | undefined) => void;
  resetGeocodedAddress: () => void;
};

/**
 * Custom hook for geocoding a Google Places placeId into a structured address.
 *
 * Example:
 * const { geocodedAddress, geocodeAddress } = useAddressGeocode();
 *
 * geocodeAddress(option.placeId);
 * ...
 * <AddressModal address={geocodedAddress} />
 */
export default function useAddressGeocode(): UseAddressGeocodeReturn {
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const [geocodedAddress, setGeocodedAddress] = useState<Partial<UserInformationAddress>>({});
  const { data: geocodeData } = useApiLocationGeocodeRetrieve(
    { placeId: selectedPlaceId || undefined },
    { query: { enabled: !!selectedPlaceId } }
  );

  useEffect(() => {
    if (geocodeData) {
      const mappedAddress = mapGeocodeResponseToAddress(geocodeData);
      setGeocodedAddress(mappedAddress);
      setSelectedPlaceId(null);
    }
  }, [geocodeData]);

  const geocodeAddress = (placeId: string | null | undefined): void => {
    if (placeId) {
      setSelectedPlaceId(placeId);
    }
  };

  const resetGeocodedAddress = (): void => {
    setGeocodedAddress({});
    setSelectedPlaceId(null);
  };

  return { geocodedAddress, geocodeAddress, resetGeocodedAddress };
}
